import { useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  OAuthProvider,
  getRedirectResult,
  linkWithPopup,
  linkWithRedirect,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type AuthCredential,
  type User,
} from 'firebase/auth';
import { auth } from '../firebase';
import { identify, track } from '../analytics';
import { claimGoogleBonus, telegramAuth } from '../game/api';
import { tgInitData, tgUserName } from '../tg';

export type AuthProviderId = 'google' | 'apple';

/** Accounts that see the in-game Admin entry (server still enforces the claim). */
const OWNER_EMAILS = ['artemkutukov@gmail.com'];
const isOwnerEmail = (email: string | null | undefined): boolean =>
  !!email && OWNER_EMAILS.includes(email.toLowerCase());

export interface AuthState {
  ready: boolean; // initial auth check finished
  user: User | null;
  busy: boolean; // a sign-in popup is in flight
  error: string | null;
  /** Whether to surface the admin console entry. Cosmetic gate — the actual
   * `admin` custom claim is enforced server-side (rules + callables). */
  isAdmin: boolean;
}

/**
 * Firebase auth for the web client. Like iOS, every visitor gets an anonymous
 * identity automatically — there is no login wall. Google is an optional
 * upgrade (via account linking, from Settings) that keeps the same uid so a
 * guest's games/leaderboard history carry over.
 */
export function useAuth() {
  const [state, setState] = useState<AuthState>({
    ready: false,
    user: null,
    // If we bounced through a full-page OAuth redirect, resume in the busy state
    // so the button keeps showing "linking…" until getRedirectResult settles —
    // otherwise the fresh page load looks idle and seems to ask you to sign in
    // again while the round-trip is still completing.
    busy: isRedirectPending(),
    error: null,
    isAdmin: false,
  });

  // Complete any redirect-based sign-in started on a previous page load. On
  // mobile browsers (and anywhere popups are blocked) we fall back to redirect,
  // so the result lands here after the round-trip.
  useEffect(() => {
    getRedirectResult(auth)
      .then((cred) => {
        if (cred?.user) track('login', { method: 'google' });
      })
      .catch(async (err) => {
        const code = (err as { code?: string })?.code ?? '';
        // The linked credential already belongs to an existing account. The
        // redirect only *tried to link* it to the guest and failed — it did NOT
        // sign us in. Reuse the credential from the error to sign into the real
        // account, otherwise the player is stranded as a guest.
        if (code === 'auth/credential-already-in-use' || code === 'auth/email-already-in-use') {
          const existing = credentialFromError('google', err) ?? credentialFromError('apple', err);
          if (existing) {
            try {
              await signInWithCredential(auth, existing);
              return;
            } catch {
              /* fall through to surfacing the original error */
            }
          }
        }
        setState((s) => ({ ...s, busy: false, error: messageFor(code, err) }));
      })
      .finally(() => {
        clearRedirectPending();
        // Drop the resumed busy flag once the redirect resolves. If it signed us
        // in, onAuthStateChanged also clears it and remounts with the new user.
        setState((s) => (s.busy ? { ...s, busy: false } : s));
      });
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // Inside Telegram: trade the signed launch data for a Firebase custom
        // token so the player gets a STABLE uid (tg<id>) — history persists
        // across sessions, unlike anonymous. Falls through to anonymous on any
        // failure so a Telegram hiccup never strands the player.
        const initData = tgInitData();
        if (initData) {
          try {
            const { token } = await telegramAuth(initData);
            await signInWithCustomToken(auth, token);
            track('login', { method: 'telegram' });
            return; // onAuthStateChanged fires again with the Telegram user.
          } catch {
            /* fall through to anonymous */
          }
        }
        // No session (first visit, or just signed out): fall back to an
        // anonymous identity so the app never shows a login wall.
        try {
          await signInAnonymously(auth);
        } catch (err) {
          const code = (err as { code?: string })?.code ?? '';
          setState((s) => ({ ...s, ready: true, user: null, busy: false, error: messageFor(code, err) }));
        }
        return; // onAuthStateChanged fires again once the anon user lands.
      }
      identify(user.uid);
      maybeClaimGoogleBonus(user);
      let isAdmin = false;
      try {
        const tok = await user.getIdTokenResult();
        isAdmin = tok.claims.admin === true || isOwnerEmail(user.email);
      } catch {
        isAdmin = isOwnerEmail(user.email);
      }
      setState((s) => ({ ...s, ready: true, user, busy: false, isAdmin }));
    });
  }, []);

  /** Upgrade the current (anonymous) session to Google, or sign in with it directly.
   *
   * Tries a popup first (no page reload — the nicer flow). Now that authDomain
   * is first-party the popup's auth iframe can reach its own storage, so it
   * works on desktop. Where popups are still blocked (mobile & in-app browsers)
   * we fall back to a full-page redirect, completed by getRedirectResult on
   * reload. Either way, if the Google account already exists as its own user we
   * sign straight into it via the credential rather than opening a second popup. */
  async function linkProvider(providerId: AuthProviderId) {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const provider =
        providerId === 'google'
          ? new GoogleAuthProvider()
          : new OAuthProvider('apple.com');
      if (provider instanceof OAuthProvider) {
        provider.addScope('name');
        provider.addScope('email');
      }
      const current = auth.currentUser;
      if (current?.isAnonymous) {
        // Link Google onto the guest so the uid (and its history) carries over.
        try {
          await linkWithPopup(current, provider);
        } catch (err) {
          const code = (err as { code?: string })?.code ?? '';
          if (code === 'auth/credential-already-in-use' || code === 'auth/email-already-in-use') {
            // Already its own account — reuse the credential from the error to
            // sign into it (no second popup, which browsers would block).
            const cred = credentialFromError(providerId, err);
            if (cred) {
              await signInWithCredential(auth, cred);
            } else {
              markRedirectPending();
              await signInWithRedirect(auth, provider);
              return;
            }
          } else if (isPopupUnavailable(code)) {
            markRedirectPending();
            await linkWithRedirect(current, provider);
            return;
          } else {
            throw err;
          }
        }
      } else {
        try {
          await signInWithPopup(auth, provider);
        } catch (err) {
          const code = (err as { code?: string })?.code ?? '';
          if (isPopupUnavailable(code)) {
            markRedirectPending();
            await signInWithRedirect(auth, provider);
            return;
          }
          throw err;
        }
      }
      track('login', { method: providerId });
      // Linking doesn't always re-fire onAuthStateChanged (same uid), so claim
      // the Google welcome bonus here too — the server call is idempotent.
      maybeClaimGoogleBonus(auth.currentUser);
      // onAuthStateChanged flips busy off and delivers the updated user.
    } catch (err) {
      clearRedirectPending();
      const code = (err as { code?: string })?.code ?? '';
      // Closing/cancelling the popup is not an error worth surfacing.
      const cancelled =
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request' ||
        code === 'auth/user-cancelled';
      setState((s) => ({ ...s, busy: false, error: cancelled ? null : messageFor(code, err) }));
    }
  }

  async function logOut() {
    await signOut(auth);
    // onAuthStateChanged will re-establish a fresh anonymous session.
  }

  return { ...state, linkProvider, logOut };
}

// Google welcome bonus (⬡100, granted once server-side). The localStorage
// marker only avoids re-calling the callable on every page load — the real
// dedupe is the `googleBonus` flag on the wallet doc.
const GBONUS_KEY = 'neonfall.googleBonus';
function maybeClaimGoogleBonus(user: User | null): void {
  if (!user || user.isAnonymous) return;
  if (!user.providerData.some((p) => p.providerId === 'google.com')) return;
  try {
    if (localStorage.getItem(`${GBONUS_KEY}.${user.uid}`)) return;
  } catch {
    /* private mode — rely on the server-side flag */
  }
  claimGoogleBonus()
    .then(() => {
      try {
        localStorage.setItem(`${GBONUS_KEY}.${user.uid}`, '1');
      } catch {
        /* private mode */
      }
    })
    .catch(() => {
      /* offline/etc — retried on a future load */
    });
}

// A full-page OAuth redirect tears down and reloads the app, so in-memory busy
// state is lost. This sessionStorage flag survives the round-trip and lets the
// resumed page show a "linking…" state until getRedirectResult resolves.
const REDIRECT_FLAG = 'neonfall.auth.redirecting';
const markRedirectPending = () => {
  try { sessionStorage.setItem(REDIRECT_FLAG, '1'); } catch { /* private mode */ }
};
const clearRedirectPending = () => {
  try { sessionStorage.removeItem(REDIRECT_FLAG); } catch { /* private mode */ }
};
const isRedirectPending = (): boolean => {
  try { return sessionStorage.getItem(REDIRECT_FLAG) === '1'; } catch { return false; }
};

/** Pull the OAuth credential out of a failed link/sign-in error so we can sign
 * straight into the existing account without prompting the user a second time. */
function credentialFromError(providerId: AuthProviderId, err: unknown): AuthCredential | null {
  try {
    return providerId === 'google'
      ? GoogleAuthProvider.credentialFromError(err as never)
      : OAuthProvider.credentialFromError(err as never);
  } catch {
    return null;
  }
}

/** Popup couldn't open (blocked, unsupported browser, or in-app webview) — fall
 * back to a full-page redirect, which can't be blocked. */
function isPopupUnavailable(code: string): boolean {
  return (
    code === 'auth/popup-blocked' ||
    code === 'auth/operation-not-supported-in-this-environment' ||
    code === 'auth/web-storage-unsupported'
  );
}

function messageFor(code: string, err: unknown): string {
  switch (code) {
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Allow popups and retry.';
    case 'auth/account-exists-with-different-credential':
      return 'That email is already linked to a different sign-in method.';
    case 'auth/operation-not-allowed':
      return 'This sign-in provider is not enabled for the project yet.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for sign-in. Add it in Firebase Auth settings.';
    default:
      return (err as { message?: string })?.message ?? 'Sign-in failed.';
  }
}

// Neon-noir callsign parts. Kept in English on purpose: like card/role
// metadata, a handle is language-independent flavour, not translated copy.
const CALLSIGN_PREFIXES = [
  'Neon', 'Chrome', 'Ghost', 'Static', 'Pulse', 'Vapor', 'Glitch', 'Razor',
  'Nova', 'Volt', 'Echo', 'Rogue', 'Cobalt', 'Phantom', 'Toxic', 'Silent',
  'Hyper', 'Crimson', 'Void', 'Zero', 'Onyx', 'Solar', 'Cyber', 'Wired',
];
const CALLSIGN_CORES = [
  'Runner', 'Ronin', 'Wraith', 'Cipher', 'Specter', 'Jackal', 'Viper',
  'Circuit', 'Drifter', 'Nomad', 'Raven', 'Shard', 'Byte', 'Fox', 'Wolf',
  'Comet', 'Blade', 'Warden', 'Synth', 'Falcon', 'Oracle', 'Reaper', 'Fang',
];

const pick = <T,>(xs: readonly T[]): T => xs[Math.floor(Math.random() * xs.length)];

/** A fresh neon-themed callsign, e.g. "GhostViper47". Used when a guest has no
 * chosen handle yet, so every anonymous player still shows up with a name. */
export function randomHandle(): string {
  const suffix = 10 + Math.floor(Math.random() * 90); // 10–99, keeps handles distinct
  return `${pick(CALLSIGN_PREFIXES)}${pick(CALLSIGN_CORES)}${suffix}`.slice(0, 24);
}

/** Best-effort display name from the signed-in account, else a random callsign. */
export function defaultHandle(user: User | null): string {
  // Inside Telegram the custom-token user has no displayName — use the name
  // Telegram gave us so runners show up as themselves, not a random callsign.
  const tgName = tgUserName();
  if (tgName) return tgName;
  const raw = user?.displayName?.trim();
  if (raw) return raw.slice(0, 24);
  const email = user?.email?.split('@')[0];
  if (email) return email.slice(0, 24);
  return randomHandle();
}
